import type { MCPContentResult, MCPMediaContent } from '../browser-client.interface.js';
import { downsampleImage } from '../image-utils.js';
import type { BrowserLogger } from '../logger.js';

const SCREENSHOT_DOWNSAMPLE_WARNING =
  '[browser_take_screenshot] Failed to downsample; returning text only';

function screenshotCapturedText(label: string | undefined): string {
  return `Screenshot captured${label ? ` (${label})` : ''}`;
}

function screenshotTextResult(label: string | undefined, hasImage: boolean): string {
  return `${screenshotCapturedText(label)}: ${hasImage ? 'success' : 'no screenshot'}`;
}

function parseScreenshotDataUrl(imageDataUrl: string): string | undefined {
  return imageDataUrl.match(/^data:image\/(?:png|jpeg);base64,(.+)$/)?.[1];
}

export interface ScreenshotResultOptions {
  imageDataUrl: string | null | undefined;
  label?: string;
  fullPage?: boolean;
  maxImageDimension: number;
  logger?: BrowserLogger;
}

export async function formatScreenshotResult(
  options: ScreenshotResultOptions
): Promise<string | MCPContentResult> {
  const base64 = options.imageDataUrl ? parseScreenshotDataUrl(options.imageDataUrl) : undefined;

  if (base64) {
    try {
      const downsampledBase64 = await downsampleImage(base64, options.maxImageDimension);
      const media: MCPMediaContent[] = [{ data: downsampledBase64, mimeType: 'image/jpeg' }];
      return {
        text: screenshotCapturedText(options.label),
        media,
        images: media,
      };
    } catch (e) {
      options.logger?.warn?.(SCREENSHOT_DOWNSAMPLE_WARNING, {
        error: e instanceof Error ? e.message : String(e),
        fullPage: options.fullPage ?? false,
      });
    }
  }

  return screenshotTextResult(options.label, Boolean(options.imageDataUrl));
}

